import React from "react";

function isDemoMode(): boolean {
  const { search } = window.location;
  return new URLSearchParams(search).has("demo");
}

function isRedactMode(): boolean {
  const { search } = window.location;
  return new URLSearchParams(search).has("redact");
}

function formatFixedNumber(number: number, fractionDigits: number): number {
  return parseFloat(number.toFixed(fractionDigits));
}

function toPascalCase(input: string): string {
  return input
    .toLowerCase()
    .replace(/(?:^|\s)\w/g, (match) => match.toUpperCase());
}

function snakeToTitle(snake: string): string {
  return snake
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function camelToSnakeCase(str: string): string {
  return str.replace(/([A-Z])/g, "_$1").toLowerCase();
}

// Stable effect/memo key for a practice selection. The array identity changes on
// every parent render, which would otherwise re-trigger every dependent fetch.
function practiceSelectionKey(practices: PracticeMessage[]): string {
  return practices
    .map((practice) => practice.wieldyId)
    .sort()
    .join(",");
}

function camelToLabel(camel: string): string {
  return (
    camel.charAt(0).toUpperCase() + camel.slice(1).replace(/([A-Z])/g, " $1")
  );
}

function replaceByWieldyId<T extends { wieldyId: string }>(
  envelopes: T[] | null,
  replacement: T,
): T[] | null {
  return (
    envelopes?.map((envelope) =>
      envelope.wieldyId === replacement.wieldyId ? replacement : envelope,
    ) ?? null
  );
}

function stringifyValues<
  T extends Record<
    string,
    | LocationInfo
    | string
    | number
    | boolean
    | null
    | Array<string>
    | DocumentList
    | undefined
  >,
>(obj: T): Record<string, string | null> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) =>
      Object.assign(acc, {
        [key]: value?.toString() ?? null,
      }),
    {} as Record<string, string | null>,
  );
}

function partialReplaceByWieldyId<
  T extends { wieldyId: string },
  R extends { wieldyId: string; practiceId: string },
>(envelopes: T[], partialEnvelope: R): T[] {
  return envelopes.map((envelope) =>
    envelope.wieldyId === partialEnvelope.wieldyId
      ? {
          ...envelope,
          ...partialEnvelope,
        }
      : envelope,
  );
}

function enumToStrings<T extends object>(enumType: T): Array<keyof T> {
  return (Object.keys(enumType) as Array<keyof T>).filter((v) =>
    Number.isNaN(Number(v)),
  );
}

/**
 * Convert an isodate to a Date type
 *
 * @param isoDate yyyy-mm-dd without timezone
 */
function isoToDate(isoDate: string): Date {
  return new Date(`${isoDate}T23:59:59`);
}

function isoNow(): string {
  return new Date().toISOString();
}

function getTodayISODate(): string {
  return new Date().toISOString().split("T")[0];
}

function preserveUrlParams(
  basePath: string,
  preserveParams: string[] = ["devmode", "demo", "redact"],
): string {
  const currentParams = new URLSearchParams(window.location.search);
  const preservedParams = new URLSearchParams();

  preserveParams.forEach((param) => {
    if (currentParams.has(param)) {
      const value = currentParams.get(param);
      if (value !== null) {
        preservedParams.set(param, value);
      }
    }
  });

  const queryString = preservedParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

function sortAlphabetically(a: string, b: string): number {
  return a.localeCompare(b);
}

/**
 * Sorts grid columns based on a specified field order.
 *
 * First checks is if the provided gridColFieldOrder is a valid array and not empty.
 * If not valid, it returns the original gridColumns array without any changes.
 *
 * If valid, it creates an object mapping each field to its index in the gridColFieldOrder array.
 * Then, it sorts the gridColumns array based on the index.
 *
 * Fields not included in gridColFieldOrder but in gridColumns are moved to the end of the sorted array,
 * maintaining their original order. This ensures that all specified fields are prioritized in the sorting,
 * while unspecified fields are still included but placed at the end.
 */
function sortColumnsByFieldOrder<C extends { field: string }>({
  gridColumns,
  gridColFieldOrder,
}: {
  gridColumns: C[];
  gridColFieldOrder: string[];
}): C[] {
  const invalidGridColFieldOrder =
    !Array.isArray(gridColFieldOrder) || !gridColFieldOrder.length;

  if (invalidGridColFieldOrder) {
    return gridColumns;
  }

  const colFieldOrderObj = gridColFieldOrder.reduce(
    (acc, field, index) => {
      acc[field] = index;
      return acc;
    },
    {} as Record<string, number>,
  );

  const sortedGridColumns = [...gridColumns].sort((a, b) => {
    // Fields not in gridColFieldOrder are assigned Infinity, moving the field to the
    // end
    const indexA = colFieldOrderObj[a.field] ?? Infinity;
    const indexB = colFieldOrderObj[b.field] ?? Infinity;
    return indexA - indexB;
  });

  return sortedGridColumns;
}

function toValueOptions<T>(
  array: T[],
  field: keyof T,
  sorter?: (a: T[keyof T], b: T[keyof T]) => number,
): T[keyof T][] {
  const values = new Set<T[keyof T]>();
  array.forEach((a) => {
    if (a[field]) {
      values.add(a[field]);
    }
  });
  const uniqueValues = Array.from(values);
  return sorter ? uniqueValues.sort(sorter) : uniqueValues;
}

function expectEnumValue<
  T extends { [key: string | number]: U },
  U extends string | number,
>(e: T, v: U): T[U] {
  const values = Object.values(e);
  if (!values.includes(v)) {
    throw new Error(
      `Unexpected enum value ${v}, expected one of ${enumToStrings(e)}`,
    );
  }
  return e[v];
}

function expectValue<T>(v: T | undefined | null): T {
  if (v === undefined || v === null) {
    throw new Error("Expected value to not be undefined or null");
  }
  return v;
}

function moneyMessageToNumber(money: MoneyMessage): number {
  return money.major + money.minor / 100; // TODO: Don't assume dollars.
}

function numberToMoneyMessage(money: number): MoneyMessage {
  const minorString = (money ?? "").toString().split(".")[1] ?? "0";
  return {
    major: Math.floor(money ?? 0),
    minor: Number(minorString.substring(0, 2)),
    code: "USD", // TODO: Don't assume dollars.
  };
}

interface HasAllProperties<T> {
  obj: Record<string, T>;
  properties: string[];
}

export interface DataTestProps extends React.HTMLAttributes<HTMLDivElement> {
  "data-testid"?: string;
}

/**
 * Returns truthy if obj has all specified properties, and each property's value is
 * either truthy or the number 0.
 */
function hasAllProperties<T>({ obj, properties }: HasAllProperties<T>) {
  return properties.every((key) => obj[key] || obj[key] === 0);
}

/**
 * Simulate a timed async call. Helpful for scenarios when
 * you want an artificial delay on the UI.
 * ex:  Load spinner flashing in & out rapidly
 */
const oneSecondInMS = 1000;
async function stall(stallTime = oneSecondInMS) {
  await new Promise((resolve) => setTimeout(resolve, stallTime));
}

/**
 * Traverse a ReactNode (elements and text) to find the
 * deepest nested child that is a string.
 * */
function getNestedChildText(element: React.ReactNode): string | null {
  if (typeof element === "string") {
    return element;
  }
  if (React.isValidElement(element) && element.props.children) {
    return getNestedChildText(element.props.children);
  }
  return null;
}

function persistLocalStorage(key: string, state: object): void {
  window.localStorage.setItem(key, JSON.stringify(state));
}

function retrieveLocalStorage<T>(key: string): T | null {
  try {
    const item = window.localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : null;
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: intentional browser console output
    console.error(`Failed to parse local storage: ${error}`);
    return null;
  }
}

function clearAppLocalStorage(): void {
  window.localStorage.clear();
}

/**
 * Format large numbers with abbreviations (K, M, B)
 * Examples:
 * - 1234 → 1.2K
 * - 1234567 → 1.2M
 * - 1234567890 → 1.2B
 * - $1234567 → $1.2M
 *
 * @param value - String or number to format (can include currency symbols)
 * @param threshold - Minimum value to start abbreviating (default: 10000)
 * @returns Formatted string with abbreviation
 */
function formatNumberWithAbbreviation(
  value: string | number,
  threshold = 10000,
): string {
  // Extract currency symbol if present
  const valueStr = String(value);
  const currencyMatch = valueStr.match(/^([^0-9-]+)/);
  const currencySymbol = currencyMatch ? currencyMatch[1] : "";

  // Extract the numeric part (remove currency symbol and commas)
  const numericStr = valueStr.replace(/[^0-9.-]/g, "");
  const numericValue = parseFloat(numericStr);

  // If not a valid number or below threshold, format without decimals
  if (Number.isNaN(numericValue) || Math.abs(numericValue) < threshold) {
    // If there's a currency symbol, format as currency without decimals
    if (currencySymbol) {
      const sign = numericValue < 0 ? "-" : "";
      return `${sign}${currencySymbol}${Math.abs(Math.round(numericValue)).toLocaleString()}`;
    }
    return valueStr;
  }

  const absValue = Math.abs(numericValue);
  const sign = numericValue < 0 ? "-" : "";

  let abbreviated: string;

  if (absValue >= 1_000_000_000) {
    // Billions
    abbreviated = `${(absValue / 1_000_000_000).toFixed(1)}B`;
  } else if (absValue >= 1_000_000) {
    // Millions
    abbreviated = `${(absValue / 1_000_000).toFixed(1)}M`;
  } else if (absValue >= 1_000) {
    // Thousands
    abbreviated = `${(absValue / 1_000).toFixed(1)}K`;
  } else {
    return valueStr;
  }

  // Remove trailing .0 (e.g., 1.0K → 1K)
  abbreviated = abbreviated.replace(/\.0([KMB])$/, "$1");

  return `${sign}${currencySymbol}${abbreviated}`;
}

export {
  camelToLabel,
  camelToSnakeCase,
  clearAppLocalStorage,
  enumToStrings,
  expectEnumValue,
  expectValue,
  formatFixedNumber,
  formatNumberWithAbbreviation,
  getNestedChildText,
  getTodayISODate,
  hasAllProperties,
  isDemoMode,
  isoNow,
  isoToDate,
  isRedactMode,
  moneyMessageToNumber,
  numberToMoneyMessage,
  partialReplaceByWieldyId,
  persistLocalStorage,
  practiceSelectionKey,
  preserveUrlParams,
  replaceByWieldyId,
  retrieveLocalStorage,
  snakeToTitle,
  sortAlphabetically,
  sortColumnsByFieldOrder,
  stall,
  stringifyValues,
  toPascalCase,
  toValueOptions,
};
